import React from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import { BRAND } from "../lib/brand";

const NAV_ITEMS = [
  { to: "/services", label: "Services" },
  { to: "/data-and-audience", label: "Data & Audience" },
  { to: "/compliance", label: "Compliance" },
  { to: "/partners", label: "Partners" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

const Logo = () => (
  <Link to="/" data-testid="brand-logo" className="flex items-center gap-2.5 group">
    <span className="relative inline-flex items-center justify-center h-8 w-8 rounded-lg bg-[#0A0E1A] overflow-hidden">
      <span className="absolute inset-0 grid-bg opacity-40" />
      <svg viewBox="0 0 24 24" className="relative h-4 w-4 text-cyan-400" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 12h14M13 5l7 7-7 7" />
      </svg>
    </span>
    <span className="font-display text-[17px] font-semibold tracking-[-0.02em] text-slate-900">
      Player<span className="text-[#0052FF]">Reach</span>
    </span>
  </Link>
);

export default function Navbar() {
  const [scrolled, setScrolled] = React.useState(false);
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled ? "bg-white/90 backdrop-blur-xl border-b border-slate-200" : "bg-white/0 border-b border-transparent"
      }`}
    >
      <div className="container-pr flex h-16 lg:h-[72px] items-center justify-between">
        <Logo />

        <nav className="hidden lg:flex items-center gap-0.5 rounded-full border border-slate-200 bg-white/60 backdrop-blur px-1.5 py-1.5">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className={({ isActive }) =>
                `px-3.5 py-1.5 text-[13.5px] font-medium rounded-full transition-colors ${
                  isActive ? "bg-slate-900 text-white" : "text-slate-700 hover:text-slate-900 hover:bg-slate-100"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link to="/contact" className="btn-primary text-[13.5px] py-2.5 px-5" data-testid="nav-cta-start-conversation">
            Start a Conversation
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>

        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button
              data-testid="nav-mobile-toggle"
              aria-label="Open menu"
              className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-md border border-slate-200 text-slate-700"
            >
              <Menu className="h-5 w-5" />
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[88%] sm:w-[420px] bg-white border-l border-slate-200">
            <div className="flex items-center justify-between mb-8">
              <Logo />
              <button onClick={() => setOpen(false)} aria-label="Close menu" className="h-10 w-10 inline-flex items-center justify-center rounded-md border border-slate-200">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex flex-col gap-1">
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-${item.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  className={({ isActive }) =>
                    `px-4 py-3 rounded-md text-base font-medium ${
                      isActive ? "bg-slate-100 text-slate-900" : "text-slate-700 hover:bg-slate-50"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </div>
            <div className="mt-8 pt-6 border-t border-slate-200">
              <Link to="/contact" onClick={() => setOpen(false)} className="btn-primary w-full text-sm" data-testid="nav-mobile-cta">
                Start a Conversation
                <ArrowUpRight className="h-4 w-4" />
              </Link>
              <p className="mt-4 text-xs text-slate-500">{BRAND.contactEmail}</p>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
